import { computed, ref } from "vue";
import { trackAppFeature } from "@/analytics/app-analytics";
// @ts-expect-error this is a known error, module not declared for typescript
import { useRegisterSW } from "virtual:pwa-register/vue";
import router from "@/router";
import { SaveState, saveDrawing } from "@/stores/SaveDrawingState";
import { Logger } from "@/logger";

function getBrowserInfo() {
  const userAgent = navigator.userAgent;
  let browserName = "Unknown";

  if (/iPad|iPhone|iPod/.test(userAgent)) {
    if (/CriOS/.test(userAgent)) {
      browserName = "Chrome";
    } else if (/FxiOS/.test(userAgent)) {
      browserName = "Firefox";
    } else if (/EdgiOS/.test(userAgent)) {
      browserName = "Edge";
    } else if (/OPiOS/.test(userAgent)) {
      browserName = "Opera";
    } else if (/Mobile.*Safari/.test(userAgent)) {
      browserName = "Safari";
    }
  } else {
    if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Firefox";
    } else if (userAgent.indexOf("SamsungBrowser") > -1) {
      browserName = "Samsung Browser";
    } else if (
      userAgent.indexOf("Opera") > -1 ||
      userAgent.indexOf("OPR") > -1
    ) {
      browserName = "Opera";
    } else if (userAgent.indexOf("Trident") > -1) {
      browserName = "Internet Explorer";
    } else if (userAgent.indexOf("Edge") > -1) {
      browserName = "Edge";
    } else if (userAgent.indexOf("Chrome") > -1) {
      browserName = "Chrome";
    } else if (userAgent.indexOf("Safari") > -1) {
      browserName = "Safari";
    }
  }
  return browserName;
}

function isMobileOrTablet() {
  const ua = navigator.userAgent;
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Tablet|iPad/.test(
    ua,
  );
}

function isIOS() {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua);
}

export const isMac = () => {
  const platform = navigator.platform.toLowerCase();
  const isTouchDevice = "ontouchend" in document;

  return platform.includes("mac") && !isTouchDevice;
};

function isPWAInstalled() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    !!(navigator as any).standalone
  );
}
function isRecommendedBrowser() {
  if (isPWAInstalled()) return true;

  const recommendedBrowsers = ["Chrome", "Edge", "Opera"];
  const browser = getBrowserInfo();

  return recommendedBrowsers.includes(browser);
}

/**
 * Demande au navigateur de ne jamais évincer le stockage de l'origine
 * (IndexedDB : plans hors ligne et brouillons). Sans cette garantie, Chrome
 * peut vider toute l'origine sous pression de stockage — en gardant le
 * `localStorage`, d'où un utilisateur toujours connecté mais sans aucun plan
 * local. Chrome accorde la demande sans dialogue si l'app est installée ou
 * très utilisée, et la refuse silencieusement sinon : on la renouvelle donc à
 * chaque session, et le résultat est attaché aux rapports Sentry pour savoir,
 * sur une perte de données, si l'appareil était protégé.
 */
export async function requestPersistentStorage(): Promise<boolean | null> {
  if (!navigator.storage?.persist) return null;
  try {
    const persisted =
      (await navigator.storage.persisted()) ||
      (await navigator.storage.persist());
    Logger.setContext("storage", { persisted });
    if (!persisted) {
      Logger.warn("usePWA.ts : persistent storage denied by the browser");
    }
    return persisted;
  } catch (e) {
    Logger.warn(`usePWA.ts : storage.persist() failed — ${e}`);
    return null;
  }
}

// Registration state below is module-level (created once) rather than inside
// `usePwaController`. Several unrelated components call this composable
// (PWAPopup, field-observations views); each call used to run its own
// `useRegisterSW()`, i.e. its own Workbox instance tracking its own "waiting"
// worker. Those independent instances could race (e.g. against the periodic
// background `r.update()` poll), so a click on "Recharger" could post
// skipWaiting through an instance whose tracked worker was already stale —
// silently doing nothing and leaving the modal stuck open forever. Sharing a
// single registration/needRefresh across all callers removes that race.
const { needRefresh, updateServiceWorker } = useRegisterSW({
  onRegistered(r: any) {
    if (!r) return;
    const NORMAL_INTERVAL = 10 * 60 * 1000;
    const RETRY_INTERVAL = 30 * 1000;
    let interval = NORMAL_INTERVAL;

    const scheduleUpdate = () => {
      setTimeout(() => {
        r.update()
          .then(() => {
            interval = NORMAL_INTERVAL;
          })
          .catch(() => {
            interval = RETRY_INTERVAL;
          })
          .finally(scheduleUpdate);
      }, interval);
    };

    scheduleUpdate();
  },
});

const RELOAD_FALLBACK_DELAY = 3 * 1000;

function reload() {
  // Persisted before the reload by the queue's pagehide handler.
  trackAppFeature("pwa_update_applied");
  updateServiceWorker();
  // Safety net: if `controllerchange` never fires (stale waiting-worker
  // reference, race with the background update poll, ...) the modal would
  // otherwise stay open forever after the user clicks "Recharger".
  setTimeout(() => {
    window.location.reload();
  }, RELOAD_FALLBACK_DELAY);
}

const shouldChangeBrowser = ref(false);
const shouldChangeBrowserAlert = ref(false);

if ("serviceWorker" in navigator) {
  // when a new service worker becomes active and claims client control, refresh page
  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", async () => {
    if (refreshing) return;
    refreshing = true;
    if (
      router.currentRoute.value.name === "canvas" &&
      SaveState.value === "changed"
    ) {
      await saveDrawing(false);
    }
    window.location.reload();
  });
}

let browserCheckStarted = false;
function ensureBrowserCheck() {
  if (browserCheckStarted) return;
  browserCheckStarted = true;

  (async () => {
    const isRecommended = await isRecommendedBrowser();
    shouldChangeBrowser.value = !isRecommended;
    shouldChangeBrowserAlert.value = !isRecommended;

    // L'invitation à installer l'app est portée par `useInstallPrompt`
    // (bouton + bandeau). Le stockage persistant est demandé à chaque session
    // authentifiée (cf. AuthLayout), installée ou non.
  })();
}

export const usePwaController = () => {
  const isDesktop = computed(() => !isMobileOrTablet());
  const isAppleOS = computed(() => isIOS() || isMac());

  ensureBrowserCheck();

  return {
    shouldChangeBrowserAlert,
    shouldChangeBrowser,
    needRefresh,
    reload,
    getBrowserInfo,
    isDesktop,
    isAppleOS,
    isMobileOrTablet,
  };
};
